import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllOrganizers, getAllUsersGlobal, getAllEventsGlobal } from '../../services/api';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

const MasterDashboardHome = () => {
    const { data: organizers = [], isLoading: loadingOrgs } = useQuery({
        queryKey: ['admin-all-organizers'],
        queryFn: getAllOrganizers
    });

    const { data: users = [], isLoading: loadingUsers } = useQuery({
        queryKey: ['admin-all-users'],
        queryFn: getAllUsersGlobal
    });

    const { data: events = [], isLoading: loadingEvents } = useQuery({
        queryKey: ['admin-all-events'],
        queryFn: getAllEventsGlobal
    });

    if (loadingOrgs || loadingUsers || loadingEvents) return <LoadingSpinner />;

    const playersCount = users.filter(u => u.role === 'player').length;
    const activeEvents = events.filter(e => new Date(e.date) >= new Date()).length;

    const stats = [
        { label: 'Organizadores Totales', value: organizers.length, icon: '🏢', color: 'bg-blue-500' },
        { label: 'Jugadores Registrados', value: playersCount, icon: '👥', color: 'bg-green-500' },
        { label: 'Eventos Globales', value: events.length, icon: '🎾', color: 'bg-primary-500' },
        { label: 'Pozos Activos', value: activeEvents, icon: '🔥', color: 'bg-orange-500' },
    ];

    return (
        <div className="space-y-8 animate-fade-in">
            <div>
                <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter italic">Visión Global del SaaS</h1>
                <p className="text-slate-500 font-medium">Panel de control de infraestructura y métricas maestras</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat) => (
                    <div key={stat.label} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group overflow-hidden relative">
                        <div className={`absolute top-0 right-0 w-24 h-24 ${stat.color} opacity-5 -mr-8 -mt-8 rounded-full transition-transform group-hover:scale-150`} />
                        <div className="flex justify-between items-start mb-4">
                            <span className="text-3xl">{stat.icon}</span>
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">En vivo</span>
                        </div>
                        <p className="text-3xl font-black text-slate-900 mb-1 leading-none">{stat.value}</p>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">{stat.label}</p>
                    </div>
                ))}
            </div>

            {/* Recent Activity (Placeholder for now) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                        <span>🚀</span> Últimos Organizadores
                    </h3>
                    <div className="space-y-4">
                        {organizers.slice(0, 5).map(org => (
                            <div key={org.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100/50">
                                <div>
                                    <p className="font-bold text-slate-900 leading-none mb-1">{org.firstName} {org.lastName}</p>
                                    <p className="text-[10px] text-slate-500 italic font-medium">{org.email}</p>
                                </div>
                                <span className="px-3 py-1 bg-green-100 text-green-700 text-[10px] font-black rounded-full uppercase italic">Activo</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-primary-900 text-white p-8 rounded-3xl shadow-2xl relative overflow-hidden group">
                     <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500 opacity-20 -mr-20 -mt-20 rounded-full blur-3xl group-hover:scale-110 transition-transform" />
                     <h3 className="text-xl font-bold mb-4 relative z-10">Estado del Sistema</h3>
                     <p className="text-primary-200 text-sm leading-relaxed mb-6 relative z-10">
                        La infraestructura está funcionando al 100%. No se han detectado latencias en las consultas de Firebase ni errores críticos en las cloud functions.
                     </p>
                     <div className="flex items-center gap-4 relative z-10">
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(74,222,128,0.8)]" />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-primary-300">API OK</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(74,222,128,0.8)]" />
                            <span className="text-[10px] font-bold uppercase tracking-widest text-primary-300">Auth OK</span>
                        </div>
                     </div>
                </div>
            </div>
        </div>
    );
};

export default MasterDashboardHome;
